import { gql, useMutation } from "@apollo/client";
import React, { useCallback, useContext, useState } from "react";
import { ContextUser } from "../../context/ContextUser";
import { Merchant } from "../../graphql/schema/merchant";
import { Toast } from "../../hooks/toast";
import { Origin } from "./components/Origin";
import { Thumbnail } from "./components/Thumbnail";
import { Checkbox, Layout, Page } from "@shopify/polaris";
import moment from "moment";
import PackageLocationPicker from "src/pages/packages/create/components/PackageLocationPicker";
import { DropZoneOwnerNumber } from "./components/DropZoneOwnerNumber";
import { useSupplierQuery } from "src/graphql/codegen_graphql";

const UPDATE_MERCHANT_INFO = gql`
  mutation updateMerchantInfo($data: MerchantInput) {
    updateMerchantInfo(data: $data)
  }
`;

const bank_info = {
  en: {
    acknowledge:
      "I acknowledge that it is my responsibility to verify the accuracy of the bank account information provided for payment transfers. I understand that any losses incurred due to incorrect bank account details provided by me are my sole responsibility, and the company is not liable for such losses.",
    agree:
      "I agree to the above terms about bank account verification and accept responsibility for ensuring the accuracy of the provided bank account information.",
  },
  km: {
    acknowledge:
      "ខ្ញុំទទួលខុសត្រូវលើព័ត៌មានគណនីធនាគារដែលខ្ញុំផ្តល់សម្រាប់ការដកប្រាក់ក្នុងប្រព័ន្ធនេះ។ ក្នុងករណីបាត់បង់ណាមួយដែលបណ្តាលមកពីព័ត៌មានគណនីធនាគារដែលខ្ញុំផ្តល់ ខ្ញុំទទួលខុសត្រូវហើយក្រុមហ៊ុនមិនត្រូវទទួលខុសត្រូវជាដាច់ខាត។",
    agree: "ខ្ញុំយល់ព្រមនឹងលក្ខខ័ណ្ឌខាងលើទាក់ទងនឹងព័ត៌មានគណនីធនាគារនិងទទួលខុសត្រូវចំពោះព័ត៌មានគណនីធនាគារ។",
  },
};

export function ProfileScreen() {
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const context = useContext(ContextUser);
  const defaultValueBank =
    context.merchant.bankAccountHoldName + "_" + context.merchant.bankAccountNumber + "_" + context.merchant.bankName;
  const [state, setState] = useState<Merchant>(context.merchant);
  const { data } = useSupplierQuery({
    variables: {
      supplierId: 535,
    },
  });
  const [updateMerchantInfo] = useMutation(UPDATE_MERCHANT_INFO, {
    refetchQueries: ["merchant"],
    onCompleted: (res) => {
      if (res.updateMerchantInfo) {
        Toast.onShow("Update profile", "success", "right");
        // window.location.pathname = "/";
      }
    },
  });

  const handleCheckBank = useCallback(
    async (name: string, value: string) => {
      let current_value_bank = "";

      if (name === "bankAccountHoldName") {
        current_value_bank = value + "_" + state.bankAccountNumber + "_" + state.bankName;
      }

      if (name === "bankAccountNumber") {
        current_value_bank = state.bankAccountHoldName + "_" + value + "_" + state.bankName;
      }

      if (name === "bankName") {
        current_value_bank = state.bankAccountHoldName + "_" + state.bankAccountNumber + "_" + value;
      }

      if (current_value_bank !== defaultValueBank) {
        setState({
          ...state,
          verifiedBankAccount: undefined,
          [name]: value,
        });
      } else {
        setState({
          ...state,
          [name]: value,
        });
      }
      await sleep(1);
      return true;
    },
    [defaultValueBank, state],
  );

  const onSubmit = (e: any) => {
    e.preventDefault();

    const current_value_bank = state.bankAccountHoldName + "_" + state.bankAccountNumber + "_" + state.bankName;

    if (current_value_bank !== defaultValueBank && !state.verifiedBankAccount) {
      Toast.onShow(
        "You are change account bank information please check agree and make sure your account bank was correct.",
        "danger",
        "right",
      );
      return;
    }

    if (state.verifiedBankAccount) {
      if (!["Alipay", "ABA"].includes(state.bankName || "")) {
        Toast.onShow("Please select bank name", "danger", "right");
        return;
      }
      if (!state.bankAccountHoldName) {
        Toast.onShow("Please input bank account name", "danger", "right");
        return;
      }
      if (!state.bankAccountNumber) {
        Toast.onShow("Please input bank account number", "danger", "right");
        return;
      }
    }

    const data = {
      logo: state.logo,
      banner: state.banner,
      address: state.address,
      coordLat: String(state.coordLat),
      coordLng: String(state.coordLng),
      contact: state.contact,
      emails: state.emails,
      vat: state.vat,
      fiscalCodeNumber: state.fiscalCodeNumber,
      bankAccountHoldName: state.bankAccountHoldName,
      bankAccountNumber: state.bankAccountNumber,
      bankName: state.bankName,
      ownerName: state.ownerName,
      ownerType: state.ownerType,
      ownerNumber: state.ownerNumber,
      frontStoreImage: state.frontStoreImage,
      abaLink: state.abaLink,
      verifiedBankAccount: state.verifiedBankAccount
        ? isNaN(Number(state.verifiedBankAccount))
          ? state.verifiedBankAccount
          : moment(new Date(Number(state.verifiedBankAccount || 0))).format("YYYY-MM-DD")
        : undefined,
    };

    updateMerchantInfo({
      variables: {
        data,
      },
    });
  };

  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section variant="oneHalf">
          <div className="user-profile compact">
            <Thumbnail state={state} setState={setState} />
            <Origin state={state} onFrontImage={(v: string) => v && setState({ ...state, frontStoreImage: v })} />
            <div className="up-contents">
              <div className="m-b">
                <div className="row m-b">
                  <div className="col-sm-6 b-r b-b">
                    <div className="el-tablo centered padded-v">
                      <div className="value">
                        <input
                          type="text"
                          value={state.bankAccountNumber!}
                          onChange={(e) =>
                            setState({
                              ...state,
                              bankAccountNumber: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            textAlign: "center",
                            backgroundColor: "transparent",
                            borderStyle: "none",
                            color: "#3E4B5B",
                            fontSize: 25,
                          }}
                          placeholder="Bank number"
                        />
                      </div>
                      <div className="label">Bank Number</div>
                    </div>
                  </div>
                  <div className="col-sm-6 b-b" style={{ overflow: "hidden" }}>
                    <div className="el-tablo centered padded-v">
                      <div className="value">
                        <div>
                          {state.contact?.split("\n").map((x) => {
                            return (
                              <div
                                style={{
                                  width: "100%",
                                  textAlign: "center",
                                  backgroundColor: "transparent",
                                  borderStyle: "none",
                                  color: "#3E4B5B",
                                  fontSize: 25,
                                }}
                              >
                                {x}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="label">Phone Number</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="padded">
              <div>
                {/* <MapGoogle state={state} setState={setState} /> */}
                <PackageLocationPicker
                  lat={Number(state.coordLat)}
                  lng={Number(state.coordLng)}
                  addressName={state.address + ""}
                  onChange={(lat, lng, address) => {
                    setState({
                      ...state,
                      coordLat: String(lat),
                      coordLng: String(lng),
                      address: address + "",
                    });
                  }}
                  defaultShow
                />
                <br />
                <div className="row">
                  <div className="col-sm-6">
                    <label>Latitude</label>
                    <input
                      type="text"
                      placeholder="Latitude"
                      className="form-control"
                      name="coordLat"
                      value={state.coordLat!}
                      onChange={(e) => {}}
                      disabled
                    />
                  </div>
                  <div className="col-sm-6">
                    <label>Longitude</label>
                    <input
                      type="text"
                      placeholder="Longitude"
                      className="form-control"
                      name="coordLng"
                      value={state.coordLng!}
                      onChange={(e) => {}}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <div className="element-wrapper">
            <div className="element-box">
              <div className="element-info">
                <div className="element-info-with-icon">
                  <div className="element-info-icon">
                    <div className="os-icon os-icon-wallet-loaded"></div>
                  </div>
                  <div className="element-info-text">
                    <h5 className="element-inner-header">Profile Settings</h5>
                    <div className="element-inner-desc">
                      Validation of the form is made possible using powerful validator plugin for L192.
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Phone number</label>
                <textarea
                  className="form-control"
                  placeholder="Enter phone number..."
                  value={state.contact!}
                  onChange={(e) => setState({ ...state, contact: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Email</label>
                <textarea
                  disabled={true}
                  name="emails"
                  className="form-control"
                  placeholder="Enter email..."
                  value={state.emails!}
                  onChange={(e) => setState({ ...state, emails: e.target.value })}
                ></textarea>
              </div>

              <div className="row">
                <div className="col-sm-6">
                  <div className="form-group">
                    <label>VAT</label>
                    <input
                      type="text"
                      placeholder="Enter vat..."
                      name="vat"
                      value={state.vat!}
                      onChange={(e) => setState({ ...state, vat: e.target.value })}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="form-group">
                    <label>Fiscal code number</label>
                    <input
                      type="text"
                      placeholder="Enter fiscal code number..."
                      name="fiscal"
                      defaultValue={state.fiscalCodeNumber!}
                      value={state.fiscalCodeNumber!}
                      onChange={(e) =>
                        setState({
                          ...state,
                          fiscalCodeNumber: e.target.value,
                        })
                      }
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  placeholder="Enter address..."
                  name="address"
                  value={state.address!}
                  onChange={(e) => setState({ ...state, address: e.target.value })}
                  className="form-control"
                />
              </div>

              <fieldset className="form-group">
                <legend>
                  <span>Account Bank</span>
                </legend>
                <div className="form-group">
                  <label>Bank name</label>
                  <select
                    className="form-control"
                    value={state.bankName ?? ""}
                    onChange={(v) => handleCheckBank("bankName", v.target.value)}
                  >
                    <option value="">Select Bank</option>
                    <option value="ABA">ABA</option>
                    <option value="Alipay">Alipay</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Bank account hold name</label>
                  <input
                    type="text"
                    placeholder="Bank account hold name..."
                    name="bank_hold"
                    value={state.bankAccountHoldName!}
                    onChange={(e) => {
                      handleCheckBank("bankAccountHoldName", e.target.value);
                    }}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Bank account number</label>
                  <input
                    type="text"
                    placeholder="Bank account number..."
                    value={state.bankAccountNumber!}
                    onChange={(e) => {
                      if (!isNaN(Number(e.target.value))) {
                        if (state.bankName === "ABA") {
                          handleCheckBank("bankAccountNumber", e.target.value);
                          // if ((state.bankAccountNumber?.length || 0) < 9) {
                          // }
                        } else {
                          handleCheckBank("bankAccountNumber", e.target.value);
                        }
                      }
                    }}
                    className="form-control"
                  />
                  {state.bankName === "ABA" && (state.bankAccountNumber?.length ?? 0) > 9 && (
                    <small className="text-danger">Invalid bank account number</small>
                  )}
                </div>
                {state.bankName === "ABA" && (
                  <div className="form-group">
                    <label>ABA Link</label>
                    <input
                      type="text"
                      placeholder="ABA link..."
                      name="aba_link"
                      className="form-control"
                      value={state.abaLink || ""}
                      onChange={(e) => setState({ ...state, abaLink: e.target.value })}
                    />
                  </div>
                )}
                {state.bankName === "Alipay" ? (
                  <div>
                    <p>{bank_info.en.acknowledge}</p>
                    <p>
                      I acknowledge that the company can only transfer the disbursement to a compatible Cambodia-based
                      bank account. As I don’t have one, I must work with my private representative or select an
                      available agent. If I select an agent, I agree to allow the agent to receive the disbursement on
                      my behalf and agree to agent’s terms and conditions. I must work with the agent to resolve any
                      issue. Again, I acknowledge that the company is not responsible for any of my loss that happens
                      between my representative or agent and myself.
                    </p>
                    <p>
                      <b>My private representative’s bank account:</b>
                      <div>
                        *Please select ABA as the bank name and fill in the bank account holder’s name and bank account
                        number of your private representative. By entering your private representative’s Cambodia-based
                        bank account name and number, you allow your representative to receive the disbursement from the
                        company on your behalf. You agree to allow your representative to transfer the disbursement to
                        your bank account above by any means at any exchange rate. You acknowledge that the company is
                        not responsible for any of my loss that happens between your representative and yourself.
                      </div>
                    </p>
                    <p>
                      <b>Available agent</b>
                      <div>
                        Bank Name: <b>{data?.supplier?.bankName || ""}</b>
                      </div>
                      <div>
                        Account Number: <b>{data?.supplier?.bankAccountNumber || ""}</b>
                      </div>
                      <div>
                        Account Holder's Name: <b>{data?.supplier?.bankAccountHolderName || ""}</b>
                      </div>
                    </p>
                    <p>
                      By selecting this option, I allow the agent to receive the disbursement from the company on my
                      behalf. I agree to allow the agent to transfer the disbursement to my bank account above by any
                      means and at an exchange rate{" "}
                      <span>not lower than 90% of the rate (ABA Buys) published by ABA in Cambodia</span>. I acknowledge
                      that the company is not responsible for any of my loss that happens between the agent and myself.
                    </p>
                    <hr />
                    <Checkbox
                      label={bank_info.en.agree}
                      checked={
                        !!state.verifiedBankAccount &&
                        !!state.bankAccountHoldName &&
                        !!state.bankAccountNumber &&
                        !!state.bankName
                      }
                      disabled={!state.bankAccountHoldName || !state.bankAccountNumber || !state.bankName}
                      onChange={(v) => {
                        if (!state.verifiedBankAccount && v === true) {
                          setState({
                            ...state,
                            verifiedBankAccount: moment(new Date()).format("YYYY-MM-DD"),
                          });
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div>
                    <p>{state.origin === "Local" ? bank_info.km.acknowledge : bank_info.en.acknowledge}</p>
                    <hr />
                    <Checkbox
                      label={state.origin === "Local" ? bank_info.km.agree : bank_info.en.agree}
                      checked={
                        !!state.verifiedBankAccount &&
                        !!state.bankAccountHoldName &&
                        !!state.bankAccountNumber &&
                        !!state.bankName
                      }
                      disabled={!state.bankAccountHoldName || !state.bankAccountNumber || !state.bankName}
                      onChange={(v) => {
                        if (!state.verifiedBankAccount && v === true) {
                          setState({
                            ...state,
                            verifiedBankAccount: moment(new Date()).format("YYYY-MM-DD"),
                          });
                        }
                      }}
                    />
                  </div>
                )}
              </fieldset>

              <fieldset className="form-group">
                <legend>
                  <span>Owner Type</span>
                </legend>

                <div className="form-group">
                  <label>Owner name</label>
                  <input
                    type="text"
                    placeholder="Owner name..."
                    name="owner_name"
                    value={state.ownerName!}
                    onChange={(e) => setState({ ...state, ownerName: e.target.value })}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Owner type</label>
                  <input
                    type="text"
                    placeholder="Owner type..."
                    name="owner_type"
                    value={state.ownerType!}
                    onChange={(e) => setState({ ...state, ownerType: e.target.value })}
                    className="form-control"
                  />
                </div>
                {/* <div className="form-group">
                      <label>Owner number</label>
                      <input
                        type="text"
                        placeholder="Owner number..."
                        name="owner_number"
                        value={state.ownerNumber!}
                        onChange={(e) => setState({ ...state, ownerNumber: e.target.value })}
                        className="form-control"
                      />
                    </div> */}
                <div className="form-group">
                  <label>Owner {state.ownerType?.toLowerCase()} picture</label>
                  <DropZoneOwnerNumber
                    value={state.ownerNumber + ""}
                    setValue={(v: any) => {
                      setState({ ...state, ownerNumber: v });
                    }}
                  />
                </div>
              </fieldset>
              <button
                disabled={
                  defaultValueBank !==
                    state.bankAccountHoldName + "_" + state.bankAccountNumber + "_" + state.bankName &&
                  !state.verifiedBankAccount
                }
                className="btn btn-primary"
                onClick={onSubmit}
              >
                Save
              </button>
              {!state.verifiedBankAccount && (
                <small className="text-danger">Please accept your agreement account bank information.</small>
              )}
            </div>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
